/**
 * Resolving a roster's declared fields against one member's values.
 *
 * Render-time only. `RosterFieldType` and `RosterFieldSpec` live in
 * `packages/models/src/streaming/RosterFields.ts` and arrive through
 * `@devour/client`, because the schema is AUTHORED and stored on
 * `StreamingTeam.rosterFields` — see that file for why it moved out of the
 * frontend, which is a story worth reading once and not three times.
 */

import {type RosterFieldSpec} from "@devour/client";

/**
 * A resolved value for one declared field, as the resolver renders it.
 *
 * `value` is `unknown` because the five types genuinely carry different
 * shapes — a PERCENTAGE is a number, a FLAG a boolean, an IMAGE a URL. The
 * per-type handlers narrow it; nothing else should read it raw.
 */
export interface ResolvedRosterField {
    spec: RosterFieldSpec;
    value: unknown;
}

/**
 * Pair a POV's declared fields with the values supplied for one member.
 *
 * Absent and null values are DROPPED rather than rendered empty. A card that
 * prints "Class year —" for every member of a roster that has no class years
 * is worse than one that omits the row: the empty label asserts the field is
 * meaningful here and merely unset, which on a POV that simply does not use
 * it is false. This is also what lets one POV's schema apply harmlessly to a
 * roster mid-migration.
 *
 * `false` SURVIVES — a FLAG's whole job is to be false sometimes, and
 * treating it as absent would make "no consent on file" and "consent
 * explicitly withheld" render identically.
 */
export function resolveRosterFields(
    specs: readonly RosterFieldSpec[],
    values: Readonly<Record<string, unknown>> | undefined,
): ResolvedRosterField[] {
    if (!specs.length || !values) return [];
    return specs
        .map(spec => ({spec, value: values[spec.key]}))
        .filter(({spec, value}) => {
            if (value === undefined || value === null) return false;
            /* An empty string is an unset text field, not a text field reading "". */
            if (typeof value === "string" && !value.trim()) return false;
            /* Everything else, including false and 0, is a real value. */
            return Boolean(spec);
        });
}
